import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { isSupabaseConfigured, supabase } from "@/services/supabase-client.service";
import {
    getTickets,
    getTicketStats,
    getTicketTrends,
    createTicket,
    updateTicketStatus,
    assignTicket,
    updateTicketUrgency,
    resolveTicket,
    deleteTicket,
} from "@/services/ticket.service";
import type { Ticket, TicketStatus, TicketUrgency, CreateTicketInput } from "@/types/ticket";
import { useToast } from "@/components/shared/Toast";

// --- Query Keys ---
export const ticketKeys = {
    all: ["tickets"] as const,
    list: (practiceId?: string) => [...ticketKeys.all, "list", practiceId] as const,
    stats: (practiceId?: string) => [...ticketKeys.all, "stats", practiceId] as const,
    trends: (practiceId?: string) => [...ticketKeys.all, "trends", practiceId] as const,
};

// --- Realtime Subscription Hook ---
export function useTicketsRealtime(practiceId?: string) {
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!practiceId || !isSupabaseConfigured()) return;

        const channel = supabase
            .channel(`tickets-realtime-${practiceId}`)
            .on(
                "postgres_changes",
                {
                    event: "*",
                    schema: "public",
                    table: "tickets",
                    filter: `practice_id=eq.${practiceId}`,
                },
                (_payload) => {
                    // Mute/Invalidate specific lists so all views sync instantly across network.
                    queryClient.invalidateQueries({ queryKey: ticketKeys.list(practiceId) });
                    queryClient.invalidateQueries({ queryKey: ticketKeys.stats(practiceId) });
                    queryClient.invalidateQueries({ queryKey: ticketKeys.trends(practiceId) });
                },
            )
            .subscribe();

        return () => {
            void supabase.removeChannel(channel);
        };
    }, [practiceId, queryClient]);
}

// --- Queries ---
export function useTicketsQuery(practiceId?: string) {
    return useQuery({
        queryKey: ticketKeys.list(practiceId),
        queryFn: () => getTickets(practiceId!),
        enabled: !!practiceId,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
}

export function useTicketStatsQuery(practiceId?: string) {
    return useQuery({
        queryKey: ticketKeys.stats(practiceId),
        queryFn: () => getTicketStats(practiceId!),
        enabled: !!practiceId,
    });
}

export function useTicketTrendsQuery(practiceId?: string) {
    return useQuery({
        queryKey: ticketKeys.trends(practiceId),
        queryFn: () => getTicketTrends(practiceId!),
        enabled: !!practiceId,
        staleTime: 1000 * 60 * 60, // 1 hour for trends
    });
}

// --- Mutations with Optimistic Updates ---
export function useUpdateTicketStatusMutation(practiceId?: string) {
    const queryClient = useQueryClient();
    const { addToast } = useToast();

    return useMutation({
        mutationFn: ({ ticketId, status }: { ticketId: string; status: TicketStatus }) =>
            updateTicketStatus(ticketId, status),
        onMutate: async ({ ticketId, status }) => {
            if (!practiceId) return;
            await queryClient.cancelQueries({ queryKey: ticketKeys.list(practiceId) });
            const previousTickets = queryClient.getQueryData<Ticket[]>(ticketKeys.list(practiceId));

            // Optimistically update the UI cache
            if (previousTickets) {
                queryClient.setQueryData<Ticket[]>(ticketKeys.list(practiceId), (old) =>
                    old?.map((t) => (t.id === ticketId ? { ...t, status } : t)),
                );
            }
            return { previousTickets };
        },
        onError: (_err, _variables, context) => {
            if (practiceId && context?.previousTickets) {
                queryClient.setQueryData(ticketKeys.list(practiceId), context.previousTickets);
            }
            addToast({ type: "error", title: "Fehler beim Aktualisieren" });
        },
        onSettled: () => {
            if (practiceId) {
                queryClient.invalidateQueries({ queryKey: ticketKeys.all });
            }
        },
    });
}

export function useResolveTicketMutation(practiceId?: string) {
    const queryClient = useQueryClient();
    const { addToast } = useToast();

    return useMutation({
        mutationFn: ({
            ticketId,
            resolution,
            resolvedBy,
        }: {
            ticketId: string;
            resolution: string;
            resolvedBy?: string;
        }) => resolveTicket(ticketId, resolution, resolvedBy),
        onMutate: async ({ ticketId, resolution, resolvedBy: _resolvedBy }) => {
            if (!practiceId) return;
            await queryClient.cancelQueries({ queryKey: ticketKeys.list(practiceId) });
            const previousTickets = queryClient.getQueryData<Ticket[]>(ticketKeys.list(practiceId));

            if (previousTickets) {
                queryClient.setQueryData<Ticket[]>(ticketKeys.list(practiceId), (old) =>
                    old?.map((t) =>
                        t.id === ticketId
                            ? {
                                  ...t,
                                  status: "resolved",
                                  resolution,
                                  resolvedAt: new Date().toISOString(),
                              }
                            : t,
                    ),
                );
            }
            return { previousTickets };
        },
        onError: (_err, _variables, context) => {
            if (practiceId && context?.previousTickets) {
                queryClient.setQueryData(ticketKeys.list(practiceId), context.previousTickets);
            }
            addToast({ type: "error", title: "Fehler beim Erledigen" });
        },
        onSettled: () => {
            if (practiceId) queryClient.invalidateQueries({ queryKey: ticketKeys.all });
        },
    });
}

export function useAssignTicketMutation(practiceId?: string) {
    const queryClient = useQueryClient();
    const { addToast } = useToast();

    return useMutation({
        mutationFn: ({ ticketId, assigneeId }: { ticketId: string; assigneeId: string }) =>
            assignTicket(ticketId, assigneeId),
        onMutate: async ({ ticketId, assigneeId }) => {
            if (!practiceId) return;
            await queryClient.cancelQueries({ queryKey: ticketKeys.list(practiceId) });
            const previousTickets = queryClient.getQueryData<Ticket[]>(ticketKeys.list(practiceId));

            if (previousTickets) {
                queryClient.setQueryData<Ticket[]>(ticketKeys.list(practiceId), (old) =>
                    old?.map((t) =>
                        t.id === ticketId
                            ? { ...t, assignedTo: assigneeId, assigneeId, status: "in_progress" }
                            : t,
                    ),
                );
            }
            return { previousTickets };
        },
        onError: (_err, _variables, context) => {
            if (practiceId && context?.previousTickets) {
                queryClient.setQueryData(ticketKeys.list(practiceId), context.previousTickets);
            }
            addToast({ type: "error", title: "Zuweisung fehlgeschlagen" });
        },
        onSettled: () => {
            if (practiceId) queryClient.invalidateQueries({ queryKey: ticketKeys.all });
        },
    });
}

export function useUpdateTicketUrgencyMutation(practiceId?: string) {
    const queryClient = useQueryClient();
    const { addToast } = useToast();

    return useMutation({
        mutationFn: ({ ticketId, urgency }: { ticketId: string; urgency: TicketUrgency }) =>
            updateTicketUrgency(ticketId, urgency),
        onMutate: async ({ ticketId, urgency }) => {
            if (!practiceId) return;
            await queryClient.cancelQueries({ queryKey: ticketKeys.list(practiceId) });
            const previousTickets = queryClient.getQueryData<Ticket[]>(ticketKeys.list(practiceId));

            if (previousTickets) {
                queryClient.setQueryData<Ticket[]>(ticketKeys.list(practiceId), (old) =>
                    old?.map((t) => (t.id === ticketId ? { ...t, urgency } : t)),
                );
            }
            return { previousTickets };
        },
        onError: (_err, _variables, context) => {
            if (practiceId && context?.previousTickets) {
                queryClient.setQueryData(ticketKeys.list(practiceId), context.previousTickets);
            }
            addToast({ type: "error", title: "Fehler beim Ändern der Dringlichkeit" });
        },
        onSettled: () => {
            if (practiceId) queryClient.invalidateQueries({ queryKey: ticketKeys.all });
        },
    });
}

export function useCreateTicketMutation(practiceId?: string) {
    const queryClient = useQueryClient();
    const { addToast } = useToast();

    return useMutation({
        mutationFn: async (input: CreateTicketInput) => {
            const createdTicket = await createTicket(practiceId!, input);
            if (!createdTicket) {
                throw new Error("Ticket creation returned no record");
            }
            return createdTicket;
        },
        onSuccess: (createdTicket) => {
            addToast({ type: "success", title: "Ticket erstellt" });
            if (practiceId) {
                queryClient.setQueryData<Ticket[]>(ticketKeys.list(practiceId), (old) => {
                    if (!old) return [createdTicket];
                    const withoutDuplicate = old.filter((ticket) => ticket.id !== createdTicket.id);
                    return [createdTicket, ...withoutDuplicate];
                });
                queryClient.invalidateQueries({ queryKey: ticketKeys.all });
            }
        },
        onError: () => {
            addToast({ type: "error", title: "Fehler beim Erstellen des Tickets" });
        },
    });
}

export function useDeleteTicketMutation(practiceId?: string) {
    const queryClient = useQueryClient();
    const { addToast } = useToast();

    return useMutation({
        mutationFn: (ticketId: string) => deleteTicket(ticketId),
        onMutate: async (ticketId) => {
            if (!practiceId) return;
            await queryClient.cancelQueries({ queryKey: ticketKeys.list(practiceId) });
            const previousTickets = queryClient.getQueryData<Ticket[]>(ticketKeys.list(practiceId));

            if (previousTickets) {
                queryClient.setQueryData<Ticket[]>(ticketKeys.list(practiceId), (old) =>
                    old?.filter((t) => t.id !== ticketId),
                );
            }
            return { previousTickets };
        },
        onError: (_err, _ticketId, context) => {
            if (practiceId && context?.previousTickets) {
                queryClient.setQueryData(ticketKeys.list(practiceId), context.previousTickets);
            }
            addToast({ type: "error", title: "Ticket konnte nicht gelöscht werden" });
        },
        onSettled: () => {
            if (practiceId) queryClient.invalidateQueries({ queryKey: ticketKeys.all });
        },
    });
}
